
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { fetchReagentById } from "@/services/reagents/fetchReagentById";
import { supabase } from "@/integrations/supabase/client";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import MessageField from "@/components/swap/MessageField";
import { sendMessageToOwner } from "@/services/swapRequests/messaging/messageService";
import { toast } from "sonner";

const MessagePage: React.FC = () => {
  const { reagentId } = useParams<{ reagentId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [reagent, setReagent] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [ownerName, setOwnerName] = useState("");

  // Load reagent data
  useEffect(() => {
    const loadReagent = async () => {
      if (!reagentId) {
        setError("No reagent specified");
        setIsLoading(false);
        return;
      }
      
      try {
        const reagentData = await fetchReagentById(reagentId);
        if (!reagentData) {
          setError("Reagent not found");
          setIsLoading(false);
          return;
        }
        
        setReagent(reagentData);
        
        // Get owner's display name
        if (reagentData.profiles) {
          setOwnerName(reagentData.profiles.display_username || reagentData.profiles.username || "Reagent Owner");
        } else {
          setOwnerName("Reagent Owner");
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error loading reagent:", err);
        setError("Failed to load reagent details");
        setIsLoading(false);
      }
    };
    
    loadReagent();
  }, [reagentId]);

  const handleSendMessage = async () => {
    if (!user || !reagentId || !message.trim() || !reagent) {
      return;
    }
    
    setIsSending(true);
    
    try {
      const success = await sendMessageToOwner(reagentId, user.id, message);
      
      if (success) {
        toast.success("Message sent successfully!");
        
        // Navigate to conversation with the reagent owner
        if (reagent.profiles && reagent.profiles.id) {
          navigate(`/conversation/${reagent.profiles.id}?reagentId=${reagentId}`);
        } else {
          // Return to reagent details if we can't navigate to conversation
          navigate(`/reagents/${reagentId}`);
        }
      } else {
        toast.error("Failed to send message");
      }
    } catch (err) {
      console.error("Error sending message:", err);
      toast.error("An error occurred while sending your message");
    } finally {
      setIsSending(false);
    }
  };
  
  // Redirect if not logged in
  if (!user) {
    return <Navigate to="/auth/login" />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow">
        <div className="mb-4">
          <Button 
            variant="outline" 
            onClick={() => navigate(-1)} 
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <Card className="p-6">
            <div className="text-center">
              <h2 className="text-xl font-semibold text-red-600 mb-2">Error</h2>
              <p>{error}</p>
              <Button className="mt-4" onClick={() => navigate(-1)}>
                Go Back
              </Button>
            </div>
          </Card>
        ) : (
          <div className="max-w-3xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">
              Send Message about {reagent.name}
            </h1>
            
            <MessageField 
              message={message} 
              setMessage={setMessage} 
              reagentOwner={ownerName} 
            />
            
            <div className="mt-6 flex justify-end space-x-4">
              <Button 
                variant="outline" 
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSendMessage} 
                disabled={isSending || !message.trim()}
              >
                {isSending ? <LoadingSpinner size="sm" /> : "Send Message"}
              </Button>
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default MessagePage;
